import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Button,
  CustomDateRangePicker,
  CustomSelect,
  SearchInput,
  SideDrawer,
  SkeletonText,
} from '@/components';
import { V2Checkbox, V2Table } from '@/common/components';
import { formatName } from '@/common/utils';
import { customerTypeLabel } from '../utils/customerTypeLabel';
import {
  combineRecipients,
  contactPhoneDigits,
} from '../utils/recipientSelection';
import { useMobileScrollLock, useOutsideClick } from '@/common/hooks';
import {
  getContacts,
  getCustomerTypes,
  createContact,
  getWindexStatus,
  getWindexContactYears,
  getWindexPendingFees,
  previewClientBulkAction,
} from '../api';
import {
  emptyWindexFilters,
  naturalSortAscending,
  normalizeContactList,
  normalizeSyncedYears,
  windexFilterBlocked,
  windexFilterMessage,
  windexFiltersToDrop,
  windexQueryParams,
} from './Integrations/windexContactFilters';
import { getCRMSegments } from '../crm/api';
import { ContactForm } from './ContactForm';
import { TemplatePhonePreview } from './TemplatePhonePreview';
import { VariableValueInput } from './VariableValueInput';
import { PlusIcon, FilterIcon } from './Icons';
// import whatsappTemplateIcon from '../assets/whatsappTemplate.png';
import userTemplateIcon from '../assets/userTemplate.png';
// import documentIcon from '../assets/document.png';
import datePickerCalendarIcon from '../assets/Inquiry/calendar.png';
import windexIcon from '../assets/Windex_icon.jpg';
import employeesIcon from '../assets/Sidebar/Employees.png';
import employeesActiveIcon from '../assets/Sidebar/Employees_active.png';
import { generateTemplateSchema } from '../utils/templateUtils';
import {
  DYNAMIC_CONTACT_FIELDS,
  contactFieldToken,
  buildRecipientVariables,
  sumPendingFees,
} from '../utils/dynamicContactVars';
import { TemplateScheduleOptions } from '../template-schedules';

const MAX_IMAGE_SIZE_BYTES = 8 * 1024 * 1024; // 8MB

const FILTER_FIELDS = [
  { key: 'customerType', label: 'Customer Type' },
  { key: 'groups', label: 'Groups' },
  { key: 'importDate', label: 'Import Date' },
  { key: 'srNo', label: 'Serial Number' },
];

const WINDEX_FILTER_FIELDS = [
  { key: 'year', label: 'Academic Year' },
  { key: 'class', label: 'Class' },
  { key: 'pendingFees', label: 'Pending Fees' },
];

const WINDEX_FILTER_KEYS = new Set(
  WINDEX_FILTER_FIELDS.map((item) => item.key),
);

async function fetchAllSegments(companyId) {
  const all = [];
  let page = 1;
  let hasMore = true;
  const maxPages = 50;

  while (hasMore && page <= maxPages) {
    const res = await getCRMSegments(companyId, { page, page_size: 100 });
    const results = Array.isArray(res)
      ? res
      : Array.isArray(res?.results)
        ? res.results
        : [];
    all.push(...results);
    hasMore = Boolean(res?.next);
    page += 1;
  }

  return all;
}

function parseSrNoRange(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Match range formats like "5-12", "5 to 12", "5..12"
  const rangeMatch = trimmed.match(/^(\d+)\s*(?:-|to|\.\.)\s*(\d+)$/i);
  if (rangeMatch) {
    let start = parseInt(rangeMatch[1], 10);
    let end = parseInt(rangeMatch[2], 10);
    if (start > end) [start, end] = [end, start];
    if (start > 0) {
      return { start, end, isRange: true };
    }
  }

  // Single number like "5"
  const single = parseInt(trimmed, 10);
  if (!isNaN(single) && single > 0) {
    return { start: single, end: single, isRange: false };
  }

  return null;
}

// function SummaryCard({ icon, iconColor, label, value }) {
//   return (
//     <div className="flex items-center gap-3 bg-surface-mint border border-primary/25 rounded-xl p-3.5">
//       <div
//         className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-white"
//         style={{ backgroundColor: iconColor }}
//       >
//         {icon}
//       </div>
//       <div className="flex flex-col min-w-0">
//         <span className="text-xs text-text-secondary">{label}</span>
//         <span className="text-[15px] font-bold text-main-black truncate">
//           {value}
//         </span>
//       </div>
//     </div>
//   );
// }

function FilterCard({
  fieldKey,
  label,
  isWindex = false,
  hideValue = false,
  onRemove,
  children,
}) {
  return (
    <div
      key={fieldKey}
      className={`flex h-11 w-full sm:w-auto items-center gap-2 rounded-xl border px-3 sm:pl-3 sm:pr-2.5 transition-colors shrink-0 ${
        isWindex ? 'border-[#C8E6C9] bg-[#F3FAF3]' : 'border-[#EBF4EA] bg-white'
      }`}
    >
      <span className="text-sm font-semibold text-[#1F2241] whitespace-nowrap shrink-0">
        {label}
      </span>
      {hideValue ? null : (
        <>
          <span className="text-sm text-[#868686] whitespace-nowrap shrink-0">
            is
          </span>
          <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-initial">
            {children}
          </div>
        </>
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="ml-auto sm:ml-1 flex items-center justify-center w-6 h-6 rounded-md text-[#868686] hover:text-[#1F2241] hover:bg-black/5 transition-colors shrink-0"
      >
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}

export function SendMessagePage({
  companyId,
  template,
  templateName,
  onCancel,
  onSend,
  isSending,
  audienceMode = 'contacts',
  segmentAudience = null,
  className = '',
}) {
  const queryClient = useQueryClient();
  const isSegmentAudience = audienceMode === 'segment';

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [windexFilters, setWindexFilters] = useState(emptyWindexFilters);
  const [segmentFilter, setSegmentFilter] = useState('');
  const [srNoFilter, setSrNoFilter] = useState('');
  const [debouncedSrNo, setDebouncedSrNo] = useState('');
  const [activeFilterFields, setActiveFilterFields] = useState([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);

  useMobileScrollLock(isFilterMenuOpen);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [page, setPage] = useState(1);
  const [matchGroups, setMatchGroups] = useState([]);
  const [pickedIds, setPickedIds] = useState(() => new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const [recipientTab, setRecipientTab] = useState('contacts'); // 'contacts' | 'direct'
  const [directNumbers, setDirectNumbers] = useState([]);
  const [directPhoneInput, setDirectPhoneInput] = useState('');

  const handleAddDirectNumbers = () => {
    if (!directPhoneInput.trim()) return;
    const tokens = directPhoneInput.split(/[\r\n,;\t]+/);
    const validToAdd = [];
    let invalidCount = 0;

    for (const token of tokens) {
      const cleaned = token.replace(/[+\s\-().]/g, '').trim();
      if (!cleaned) continue;
      if (/^\d{10,15}$/.test(cleaned)) {
        if (!directNumbers.includes(cleaned) && !validToAdd.includes(cleaned)) {
          validToAdd.push(cleaned);
        }
      } else {
        invalidCount += 1;
      }
    }

    if (invalidCount > 0) {
      toast.error(
        `${invalidCount} number(s) skipped. Phone numbers must be 10-15 digits including country code (e.g. 919876543210).`,
      );
    }

    if (validToAdd.length > 0) {
      setDirectNumbers((prev) => [...prev, ...validToAdd]);
      setDirectPhoneInput('');
    } else if (invalidCount === 0) {
      setDirectPhoneInput('');
    }
  };

  const handleRemoveDirectNumber = (numToRemove) => {
    setDirectNumbers((prev) => prev.filter((n) => n !== numToRemove));
  };

  const handleClearDirectNumbers = () => {
    setDirectNumbers([]);
  };

  const [templateValues, setTemplateValues] = useState({});
  const [mediaFile, setMediaFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [carouselMediaFiles, setCarouselMediaFiles] = useState({});
  const [compressingCards, setCompressingCards] = useState({});

  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false);
  const [sendMode, setSendMode] = useState('send_now');
  const scheduleOptionsRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSrNo(srNoFilter);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [srNoFilter]);

  useOutsideClick(
    filterMenuRef,
    () => setIsFilterMenuOpen(false),
    isFilterMenuOpen,
  );

  const buildContactParams = (overrides = {}) => {
    const params = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (!feesOn && typeFilter) params.filter = typeFilter;
    if (windexConnected)
      Object.assign(params, windexQueryParams(windexFilters));
    if (segmentFilter) params.segment = String(segmentFilter);
    if (dateStart && dateEnd) {
      params.import_start_date = dateStart;
      params.import_end_date = dateEnd;
    }
    return { ...params, ...overrides };
  };

  const templateSchema = useMemo(
    () => (template ? generateTemplateSchema(template) : null),
    [template],
  );

  const areTemplateVariablesFilled = useMemo(() => {
    if (!templateSchema?.form_fields?.length) return true;
    const fieldsToCheck =
      template?.category === 'AUTHENTICATION'
        ? templateSchema.form_fields.slice(0, 1)
        : templateSchema.form_fields;
    return fieldsToCheck.every((field) =>
      Boolean(templateValues[field.id]?.trim()),
    );
  }, [templateSchema, template?.category, templateValues]);

  const isTemplateMediaUploaded = useMemo(() => {
    // 1. Check generated schema media_field
    if (templateSchema?.media_field?.required && !mediaFile) {
      return false;
    }
    // 2. Direct check on template components
    if (Array.isArray(template?.components)) {
      const headerComp = template.components.find(
        (c) => (c.type || '').toUpperCase() === 'HEADER',
      );
      const format = (headerComp?.format || '').toUpperCase();
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format) && !mediaFile) {
        return false;
      }
    }
    // 3. Direct check on template root header properties
    const rootFormat = (
      template?.header_type ||
      template?.header_format ||
      template?.header?.format ||
      template?.header?.type ||
      ''
    ).toUpperCase();
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(rootFormat) && !mediaFile) {
      return false;
    }

    // 4. Check carousel media in schema
    if (templateSchema?.carousel_media?.length > 0) {
      const missing = templateSchema.carousel_media.some(
        (m) => !carouselMediaFiles[m.cardIndex],
      );
      if (missing) return false;
    }

    // 5. Check carousel media directly in cards
    const rawCards =
      (Array.isArray(template?.cards) && template.cards) ||
      template?.components?.find(
        (c) => (c.type || '').toUpperCase() === 'CAROUSEL',
      )?.cards ||
      [];
    if (rawCards.length > 0) {
      const missingCard = rawCards.some((card, idx) => {
        const cHeader = card.components?.find(
          (c) => (c.type || '').toUpperCase() === 'HEADER',
        );
        const cFormat = (
          card.header_type ||
          card.header_format ||
          cHeader?.format ||
          ''
        ).toUpperCase();
        return (
          ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(cFormat) &&
          !carouselMediaFiles[idx]
        );
      });
      if (missingCard) return false;
    }

    return true;
  }, [template, templateSchema, mediaFile, carouselMediaFiles]);

  const isTemplateReady = areTemplateVariablesFilled && isTemplateMediaUploaded;

  const { data: windexStatus } = useQuery({
    queryKey: ['windex-status', companyId],
    queryFn: () => getWindexStatus(companyId),
    enabled: !!companyId && !isSegmentAudience,
    retry: false,
  });
  const windexConnected = !!windexStatus?.is_connected;

  useEffect(() => {
    if (windexConnected) return;
    setActiveFilterFields((prev) =>
      prev.filter((key) => !WINDEX_FILTER_KEYS.has(key)),
    );
    setWindexFilters(emptyWindexFilters());
  }, [windexConnected]);
  const feesOn = activeFilterFields.includes('pendingFees');
  const feesNeedYear = feesOn && !windexFilters.year;
  const feesNeedClass = feesOn && !windexFilters.class;

  const { data: yearsData, error: yearsError } = useQuery({
    queryKey: ['windex-contact-years', companyId],
    queryFn: () => getWindexContactYears(companyId),
    enabled: windexConnected && !!companyId && !isSegmentAudience,
    retry: false,
  });
  const syncedYears = normalizeSyncedYears(yearsData);

  const parsedSrNo = useMemo(
    () => parseSrNoRange(debouncedSrNo),
    [debouncedSrNo],
  );

  const {
    data: contactsData,
    isLoading: isContactsLoading,
    error: contactsError,
  } = useQuery({
    queryKey: [
      'send-message-contacts',
      companyId,
      debouncedSearch,
      page,
      feesOn ? '' : typeFilter,
      windexFilters.year,
      windexFilters.class,
      feesOn,
      segmentFilter,
      dateStart,
      dateEnd,
      parsedSrNo?.start,
      parsedSrNo?.end,
    ],
    queryFn: async () => {
      if (feesOn) {
        if (!windexFilters.year || !windexFilters.class) {
          return { results: [] };
        }
        const data = await getWindexPendingFees(companyId, {
          year: windexFilters.year,
          studentClass: windexFilters.class,
        });
        return normalizeContactList(data);
      }
      if (parsedSrNo) {
        // Calculate the slice of Sr No for current page (10 per page)
        const currStartSr = parsedSrNo.start + (page - 1) * 10;
        const currEndSr = Math.min(parsedSrNo.end, currStartSr + 9);

        if (currStartSr > parsedSrNo.end) {
          return { results: [], count: 0, isRanged: true };
        }

        const startBackendPage = Math.ceil(currStartSr / 10);
        const endBackendPage = Math.ceil(currEndSr / 10);

        const pagePromises = [];
        for (let p = startBackendPage; p <= endBackendPage; p++) {
          pagePromises.push(
            getContacts(
              companyId,
              buildContactParams(p > 1 ? { page: p } : {}),
            ),
          );
        }
        const responses = await Promise.all(pagePromises);

        const allResults = [];
        let totalCount = 0;
        responses.forEach((res, idx) => {
          const currPage = startBackendPage + idx;
          if (res?.count) totalCount = res.count;
          (res?.results || []).forEach((c, itemIdx) => {
            const absoluteSr = (currPage - 1) * 10 + itemIdx + 1;
            if (absoluteSr >= currStartSr && absoluteSr <= currEndSr) {
              allResults.push({
                ...c,
                _srNo: absoluteSr,
              });
            }
          });
        });

        const effectiveEnd = totalCount
          ? Math.min(totalCount, parsedSrNo.end)
          : parsedSrNo.end;
        const rangeTotalCount = Math.max(
          0,
          effectiveEnd - parsedSrNo.start + 1,
        );

        return {
          results: allResults,
          count: rangeTotalCount,
          isRanged: true,
        };
      }

      return getContacts(
        companyId,
        buildContactParams(page > 1 ? { page } : {}),
      );
    },
    enabled:
      !!companyId && !isSegmentAudience && !feesNeedYear && !feesNeedClass,
  });

  const { data: customerTypesData } = useQuery({
    queryKey: ['customer-types', companyId],
    queryFn: () => getCustomerTypes(companyId),
    enabled: !!companyId && !isSegmentAudience,
  });
  const customerTypes = customerTypesData || [];
  const typeFilterOptions = [
    { value: '', label: 'All Type' },
    ...customerTypes.map((t) => ({ value: t.name, label: t.name })),
  ];

  const { data: segmentsData, isLoading: isSegmentsLoading } = useQuery({
    queryKey: ['send-message-segments', companyId],
    queryFn: () => fetchAllSegments(companyId),
    enabled: !!companyId && !isSegmentAudience,
  });
  const segments = segmentsData || [];
  const segmentFilterOptions = [
    { value: '', label: 'All Groups' },
    ...segments.map((s) => ({
      value: String(s.id),
      label: s.name || `Segment ${s.id}`,
    })),
  ];

  const contacts = useMemo(
    () => contactsData?.results || [],
    [contactsData?.results],
  );
  const phoneByIdRef = useRef(new Map());
  useEffect(() => {
    contacts.forEach((contact) => {
      const digits = contactPhoneDigits(contact);
      if (digits) phoneByIdRef.current.set(contact.id, digits);
    });
  }, [contacts]);
  const count = contactsData?.count || 0;

  const filterMessage = feesNeedYear
    ? 'Choose an academic year'
    : windexFilterMessage(contactsError) || windexFilterMessage(yearsError);

  const visibleContacts = useMemo(() => {
    const rows = feesOn ? contacts.slice((page - 1) * 10, page * 10) : contacts;
    if (contactsData?.isRanged) {
      return rows;
    }
    return rows.map((contact, index) => ({
      ...contact,
      _srNo: (page - 1) * 10 + index + 1,
    }));
  }, [contacts, contactsData?.isRanged, page, feesOn]);

  const handleAddFilterField = (key) => {
    if (windexFilterBlocked(key, activeFilterFields)) return;
    if (key === 'pendingFees') setPage(1);
    setActiveFilterFields((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
    setIsFilterMenuOpen(false);
  };

  const handleRemoveFilterField = (key) => {
    setActiveFilterFields((prev) => prev.filter((f) => f !== key));
    if (key === 'customerType') setTypeFilter('');
    if (key === 'groups') setSegmentFilter('');
    if (key === 'srNo') setSrNoFilter('');
    if (key === 'importDate') {
      setDateStart('');
      setDateEnd('');
    }
    if (WINDEX_FILTER_KEYS.has(key)) {
      const drop = windexFiltersToDrop(key);
      setWindexFilters((prev) => {
        const next = { ...prev };
        drop.forEach((item) => {
          next[item] = item === 'pendingFees' ? false : '';
        });
        return next;
      });
      setActiveFilterFields((prev) =>
        prev.filter((item) => !drop.includes(item)),
      );
    }
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setTypeFilter('');
    setSegmentFilter('');
    setSrNoFilter('');
    setDateStart('');
    setDateEnd('');
    setWindexFilters(emptyWindexFilters());
    setActiveFilterFields([]);
    setPage(1);
  };

  const createContactMutation = useMutation({
    mutationFn: (payload) => createContact({ companyId, payload }),
    onSuccess: (data) => {
      toast.success('Contact added successfully!');
      queryClient.invalidateQueries({
        queryKey: ['send-message-contacts', companyId],
      });
      setIsCreateContactOpen(false);
      if (data && data.id) {
        setPickedIds((prev) => new Set(prev).add(data.id));
      }
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Failed to add contact.',
      );
    },
  });

  const selectionCriteria = useMemo(
    () => ({
      search: debouncedSearch || '',
      filter: !feesOn && typeFilter ? typeFilter : '',
      segment: segmentFilter ? String(segmentFilter) : null,
      import_start_date: dateStart && dateEnd ? dateStart : null,
      import_end_date: dateStart && dateEnd ? dateEnd : null,
    }),
    [debouncedSearch, feesOn, typeFilter, segmentFilter, dateStart, dateEnd],
  );
  const selectionKey = JSON.stringify([
    selectionCriteria.search,
    selectionCriteria.filter,
    selectionCriteria.segment,
    selectionCriteria.import_start_date,
    selectionCriteria.import_end_date,
  ]);
  const toSelection = (group) => ({
    mode: 'all_matching',
    excluded_ids: group.excluded,
    search: group.search,
    filter: group.filter,
    segment: group.segment,
    import_start_date: group.import_start_date,
    import_end_date: group.import_end_date,
  });

  const contactTypeNames = (contact) => {
    if (
      Array.isArray(contact?.customer_types) &&
      contact.customer_types.length
    ) {
      return contact.customer_types
        .map((type) => (type && typeof type === 'object' ? type.name : type))
        .filter(Boolean);
    }
    const one = contact?.customer_type;
    if (one && typeof one === 'object') return one.name ? [one.name] : [];
    if (one) return [one];
    return [];
  };

  const rowMatchesGroup = (row, group) => {
    if (!group.filter) return true;
    if (selectionCriteria.filter && group.filter === selectionCriteria.filter) {
      return true;
    }
    return contactTypeNames(row).includes(group.filter);
  };

  const isRowSelected = (contact) => {
    if (!contact) return false;
    if (pickedIds.has(contact.id)) return true;
    return matchGroups.some((group) => {
      if (group.excluded.includes(contact.id)) return false;
      return rowMatchesGroup(contact, group);
    });
  };

  const toggleSelection = (id) => {
    const contact = contacts.find((row) => row.id === id);
    if (!contact) return;
    if (isRowSelected(contact)) {
      setMatchGroups((prev) =>
        prev.map((group) => {
          if (group.excluded.includes(id) || !rowMatchesGroup(contact, group)) {
            return group;
          }
          return { ...group, excluded: [...group.excluded, id] };
        }),
      );
      setPickedIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    const covered = matchGroups.some((group) =>
      rowMatchesGroup(contact, group),
    );
    if (covered) {
      setMatchGroups((prev) =>
        prev.map((group) => {
          if (!group.excluded.includes(id)) return group;
          return {
            ...group,
            excluded: group.excluded.filter((item) => item !== id),
          };
        }),
      );
      return;
    }
    setPickedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setMatchGroups([]);
    setPickedIds(new Set());
  };

  const currentGroup = matchGroups.find((group) => group.key === selectionKey);
  const headerChecked = !!currentGroup && currentGroup.excluded.length === 0;
  const headerPartial = !!currentGroup && currentGroup.excluded.length > 0;

  const groupSelections = useMemo(
    () => matchGroups.map(toSelection),
    [matchGroups],
  );

  const { data: groupCounts, isFetching: isPreviewing } = useQuery({
    queryKey: ['send-message-preview-groups', companyId, groupSelections],
    queryFn: async () => {
      const rows = await Promise.all(
        groupSelections.map((selection) =>
          previewClientBulkAction({ companyId, selection }),
        ),
      );
      return rows.map((row) => row?.matched_count ?? 0);
    },
    enabled: !!companyId && groupSelections.length > 0 && !isSegmentAudience,
  });

  const savedSelectionCount =
    (groupCounts || []).reduce((sum, n) => sum + n, 0) + pickedIds.size;

  const recipientSummary = useMemo(() => {
    const selectedPhones = [];
    pickedIds.forEach((id) => {
      const phone = phoneByIdRef.current.get(id);
      if (phone) selectedPhones.push(phone);
    });
    return combineRecipients({
      savedCount: savedSelectionCount,
      selectedPhones,
      directNumbers,
    });
  }, [savedSelectionCount, pickedIds, directNumbers]);

  const totalRecipients = isSegmentAudience
    ? Number(segmentAudience?.contactCount) || 0
    : recipientSummary.total;

  const toggleAllSelection = async () => {
    if (isSelectingAll || count === 0) return;

    if (currentGroup) {
      setMatchGroups((prev) =>
        prev.filter((group) => group.key !== selectionKey),
      );
      return;
    }

    if (
      !selectionCriteria.filter &&
      matchGroups.some((group) => group.filter)
    ) {
      setMatchGroups((prev) => prev.filter((group) => !group.filter));
      return;
    }

    if (parsedSrNo || feesOn) {
      setIsSelectingAll(true);
      const loadingToast = toast.loading(
        parsedSrNo
          ? 'Selecting all contacts in range...'
          : 'Selecting all contacts...',
      );
      try {
        let rangedIds = [];
        if (feesOn) {
          if (!windexFilters.year || !windexFilters.class) {
            toast.dismiss(loadingToast);
            setIsSelectingAll(false);
            toast.error('Select an academic year and class');
            return;
          }
          const res = await getWindexPendingFees(companyId, {
            year: windexFilters.year,
            studentClass: windexFilters.class,
          }).then(normalizeContactList);
          rangedIds = (res?.results || []).map((contact) => contact.id);
        } else {
          const startBackendPage = Math.ceil(parsedSrNo.start / 10);
          const endBackendPage = Math.ceil(parsedSrNo.end / 10);
          const responses = await Promise.all(
            Array.from(
              { length: endBackendPage - startBackendPage + 1 },
              (_, idx) =>
                getContacts(
                  companyId,
                  buildContactParams(
                    startBackendPage + idx > 1
                      ? { page: startBackendPage + idx }
                      : {},
                  ),
                ),
            ),
          );
          responses.forEach((res, idx) => {
            const currPage = startBackendPage + idx;
            (res?.results || []).forEach((contact, itemIdx) => {
              const absoluteSr = (currPage - 1) * 10 + itemIdx + 1;
              if (
                absoluteSr >= parsedSrNo.start &&
                absoluteSr <= parsedSrNo.end
              ) {
                rangedIds.push(contact.id);
              }
            });
          });
        }
        if (rangedIds.length > 1000) {
          toast.error(
            'Max 1000 customers per send. Deselect some, or send in batches.',
            { id: loadingToast },
          );
          return;
        }
        setMatchGroups([]);
        setPickedIds(new Set(rangedIds));
        toast.success(`Selected ${rangedIds.length} contact(s).`, {
          id: loadingToast,
        });
      } catch {
        toast.error('Failed to select all contacts.', { id: loadingToast });
      } finally {
        setIsSelectingAll(false);
      }
      return;
    }

    setMatchGroups((prev) => [
      ...prev.filter((group) => group.key !== selectionKey),
      {
        key: selectionKey,
        ...selectionCriteria,
        excluded: [],
        label: selectionCriteria.filter || 'All contacts',
      },
    ]);
  };

  const handleSend = async () => {
    if (isSegmentAudience) {
      const contactCount = Number(segmentAudience?.contactCount) || 0;
      if (contactCount <= 0) {
        toast.error(
          'No WhatsApp recipients. Contacts need opt-in consent and a phone number.',
        );
        return;
      }
    } else {
      if (
        (matchGroups.length > 0 || pickedIds.size > 0) &&
        (feesNeedYear || feesNeedClass)
      ) {
        toast.error('Select an academic year and class');
        return;
      }
      if (matchGroups.length > 0 && (isPreviewing || !groupCounts)) {
        toast.error('Still counting recipients.');
        return;
      }
      if (
        (matchGroups.length > 0 || pickedIds.size > 0) &&
        savedSelectionCount === 0
      ) {
        toast.error('No contacts match this selection.');
        return;
      }
      if (matchGroups.some((group) => group.excluded.length > 1000)) {
        toast.error('excluded_ids cannot exceed 1,000.');
        return;
      }

      if (recipientSummary.total === 0) {
        toast.error('Add at least one saved contact or direct number.');
        return;
      }
    }

    const scheduleValidation = scheduleOptionsRef.current?.validate?.() || {
      ok: true,
    };
    if (!scheduleValidation.ok) {
      toast.error(scheduleValidation.message || 'Invalid schedule settings.');
      return;
    }

    if (!isTemplateReady) {
      toast.error('Please fill in all required template fields and image.');
      return;
    }

    const { sendMode: mode, schedule } =
      scheduleOptionsRef.current?.getSchedulePayload?.() || {
        sendMode: 'send_now',
        schedule: null,
      };

    let base64Data = null;
    let filename = null;
    if (mediaFile) {
      base64Data = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(mediaFile);
      });
      filename = mediaFile.name;
    }

    const carouselMediaPayload = {};
    if (Object.keys(carouselMediaFiles).length > 0) {
      await Promise.all(
        Object.entries(carouselMediaFiles).map(async ([index, file]) => {
          const b64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.readAsDataURL(file);
          });
          carouselMediaPayload[index] = {
            base64Data: b64,
            filename: file.name,
            mediaType:
              templateSchema.carousel_media?.find(
                (m) => m.cardIndex === parseInt(index),
              )?.type || null,
          };
        }),
      );
    }

    const selectedFeeContacts = feesOn
      ? contacts.filter((contact) => pickedIds.has(contact.id))
      : [];
    if (feesOn && selectedFeeContacts.length > 0) {
      const totalFees =
        sumPendingFees(selectedFeeContacts).toLocaleString('en-IN');
      const ok = window.confirm(
        `Total students: ${selectedFeeContacts.length}\nTotal outstanding fees: ₹${totalFees}\nTemplate: ${templateName || template?.name || 'Selected template'}\n\nQueue this campaign?`,
      );
      if (!ok) return;
    }

    let sendIds = [];
    let oneSelection = null;
    let selections = null;
    const multi =
      !isSegmentAudience &&
      (matchGroups.length > 1 ||
        (matchGroups.length >= 1 && pickedIds.size > 0));
    if (!isSegmentAudience && matchGroups.length > 20) {
      toast.error('Max 20 Select All groups per send.');
      return;
    }
    if (multi) {
      selections = matchGroups.map((group) => {
        const item = {
          mode: 'all_matching',
          excluded_ids: group.excluded,
        };
        if (group.filter) item.filter = `customer_type=${group.filter}`;
        if (group.search) item.search = group.search;
        if (group.segment) item.segment = group.segment;
        if (group.import_start_date && group.import_end_date) {
          item.import_start_date = group.import_start_date;
          item.import_end_date = group.import_end_date;
        }
        return item;
      });
      sendIds = Array.from(pickedIds);
    } else if (!isSegmentAudience && matchGroups.length === 1) {
      oneSelection = groupSelections[0];
    } else if (!isSegmentAudience && pickedIds.size > 0) {
      if (pickedIds.size > 1000) {
        toast.error(
          'Max 1000 customers per send. Deselect some, or send in batches.',
        );
        return;
      }
      sendIds = Array.from(pickedIds);
    }

    const payload = {
      template: templateName || template?.name,
      schema: templateSchema,
      values: templateValues,
      mediaType: templateSchema?.media_field
        ? templateSchema.media_field.type
        : null,
      base64Data,
      filename,
      carouselMedia: carouselMediaPayload,
      year:
        isSegmentAudience || !windexConnected || sendIds.length || selections
          ? undefined
          : windexFilters.year || undefined,
      studentClass:
        isSegmentAudience || !windexConnected || sendIds.length || selections
          ? undefined
          : windexQueryParams(windexFilters).class,
      customer_type: oneSelection?.filter || undefined,
      segment: oneSelection
        ? oneSelection.segment || undefined
        : sendIds.length || isSegmentAudience
          ? undefined
          : segmentFilter || undefined,
      sendMode: mode,
      schedule,
      contact_numbers:
        recipientSummary.directUnique.length > 0
          ? recipientSummary.directUnique
          : undefined,
      recipient_variables: selectedFeeContacts.length
        ? buildRecipientVariables(selectedFeeContacts, windexFilters.year)
        : undefined,
    };
    if (selections) payload.selections = selections;
    else if (oneSelection) payload.selection = oneSelection;
    try {
      await onSend(isSegmentAudience ? [] : sendIds, payload);
    } catch (err) {
      const text = windexFilterMessage(err);
      if (text) toast.error(text);
    }
  };

  const sendContactColumns = [
    {
      id: 'select',
      label: '',
      defaultWidth: 48,
      minWidth: 48,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-4" height="h-4" className="mx-auto" />,
      header: () =>
        isSelectingAll ? (
          <div className="w-4 h-4 border-2 border-[#2E7D32] border-t-transparent rounded-full animate-spin mx-auto" />
        ) : (
          <V2Checkbox
            checked={headerChecked}
            indeterminate={headerPartial}
            onChange={toggleAllSelection}
            disabled={isSelectingAll}
            isHeader
          />
        ),
      cell: (contact) => (
        <V2Checkbox
          checked={isRowSelected(contact)}
          onChange={() => toggleSelection(contact.id)}
        />
      ),
    },
    {
      id: 'sr',
      label: 'Sr No.',
      defaultWidth: 72,
      minWidth: 60,
      resizable: true,
      skeleton: <SkeletonText width="w-7" />,
      cell: (contact) => (
        <span className="truncate block text-xs font-medium text-[#64748B]">
          {contact._srNo}
        </span>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 220,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-28" />,
      cell: (contact) => (
        <span
          className="truncate block font-medium text-sm text-[#0F172A]"
          title={contact.name || ''}
        >
          {formatName(contact.name) || '-'}
        </span>
      ),
    },
    {
      id: 'phone',
      label: 'Phone',
      defaultWidth: 150,
      minWidth: 120,
      resizable: true,
      skeleton: <SkeletonText width="w-24" />,
      cell: (contact) => (
        <span className="truncate block text-sm text-[#1E293B]">
          {contact.phone}
        </span>
      ),
    },
    ...(feesOn
      ? [
          {
            id: 'class',
            label: 'Class',
            defaultWidth: 120,
            minWidth: 90,
            resizable: true,
            skeleton: <SkeletonText width="w-16" />,
            cell: (contact) => (
              <span className="truncate block text-sm">
                {contact.student_class || windexFilters.class || '-'}
              </span>
            ),
          },
          {
            id: 'pendingFees',
            label: 'Pending fee',
            defaultWidth: 130,
            minWidth: 110,
            resizable: true,
            skeleton: <SkeletonText width="w-16" />,
            cell: (contact) => (
              <span className="truncate block text-sm font-semibold text-[#DC2626]">
                {contact.pending_fee ?? '-'}
              </span>
            ),
          },
        ]
      : []),
    {
      id: 'type',
      label: 'Type',
      defaultWidth: 160,
      minWidth: 100,
      resizable: true,
      skeleton: <SkeletonText width="w-16" />,
      cell: (contact) => {
        const typeLabel = customerTypeLabel(contact);
        return typeLabel ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#334155] border border-[#E2E8F0] truncate max-w-full"
            title={typeLabel}
          >
            {typeLabel}
          </span>
        ) : (
          <span className="text-text-muted text-xs">-</span>
        );
      },
    },
  ];
  const isScheduleMode = sendMode !== 'send_now';

  const getSendButtonLabel = () => {
    const actionText = isScheduleMode ? 'Schedule' : 'Send';
    if (isSegmentAudience) {
      const count = Number(segmentAudience?.contactCount) || 0;
      return `${actionText} to ${count} Recipient${count !== 1 ? 's' : ''}`;
    }
    if (totalRecipients === 0) {
      return actionText;
    }
    return `${actionText} to ${totalRecipients} Recipient${totalRecipients !== 1 ? 's' : ''}`;
  };

  return (
    <div
      className={`flex flex-col gap-5 laptop:gap-3 w-full mx-auto flex-1 animate-fadeIn relative ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 sm:gap-3 w-full shrink-0 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 laptop:w-8 laptop:h-8 rounded-[8px] bg-white border border-primary text-primary hover:bg-surface-mint flex items-center justify-center transition-colors focus:outline-none shrink-0"
            aria-label="Back"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-base sm:text-xl laptop:text-lg font-bold text-main-black truncate shrink min-w-0 max-w-xs sm:max-w-md">
            {templateName || 'New Message'}
          </h1>
        </div>

        {/* Recipients Info in Header */}
        <div className="flex items-center gap-2 sm:gap-3 bg-surface-mint px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl border border-primary/25 shrink-0">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 bg-[#50AB3D] text-white">
            <img
              src={userTemplateIcon}
              alt=""
              className="w-3.5 h-3.5 sm:w-4 sm:h-4 object-contain brightness-0 invert"
            />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] sm:text-[11px] font-medium leading-[1] text-text-secondary">
              {isSegmentAudience ? 'Group audience' : 'Recipients'}
            </span>
            <span className="text-xs sm:text-[14px] font-bold leading-[1.2] text-main-black mt-0.5 sm:mt-1 truncate">
              {isSegmentAudience
                ? `${Number(segmentAudience?.contactCount) || 0} contacts`
                : totalRecipients > 0
                  ? `${totalRecipients} Total Recipient${totalRecipients === 1 ? '' : 's'}`
                  : '0 Total Recipients'}
            </span>
          </div>
        </div>
      </div>

      {/* Two column area */}
      <div className="flex flex-col lg:flex-row flex-1 gap-5 laptop:gap-3 w-full items-start">
        {/* LEFT: fills parent slot */}
        <div className="w-full lg:flex-[7] lg:basis-0 lg:min-w-0 flex flex-col gap-4 laptop:gap-2.5 bg-form-background-light rounded-2xl border border-surface-mint p-4 laptop:p-3">
          {/* Summary cards removed to save vertical space */}

          <div className="flex flex-col gap-[10px] laptop:gap-1.5 animate-fadeIn shrink-0">
            <h3 className="text-[18px] leading-[26px] laptop:text-[16px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
              Sending Method
            </h3>
            <div className="bg-white rounded-[12px] p-4 laptop:p-3 flex flex-col gap-4 laptop:gap-2 w-full">
              <div className="w-full max-w-2xl">
                <TemplateScheduleOptions
                  ref={scheduleOptionsRef}
                  disabled={isSending || isCompressing}
                  onModeChange={setSendMode}
                  modeButtonClassName="laptop:!h-9"
                />
              </div>
            </div>
          </div>

          {(templateSchema?.media_field ||
            templateSchema?.carousel_media?.length > 0 ||
            templateSchema?.form_fields?.length > 0) && (
            <div className="flex flex-col gap-[10px] laptop:gap-1.5 animate-fadeIn shrink-0">
              <h3 className="text-[18px] leading-[26px] laptop:text-[16px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
                Template Details
              </h3>
              <div className="bg-white rounded-[12px] p-4 laptop:p-3 flex flex-col gap-4 laptop:gap-2.5 w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 laptop:gap-2.5">
                  {templateSchema?.media_field && (
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold text-text-secondary">
                        {templateSchema.media_field.label}{' '}
                        <span className="text-primary">*</span>
                      </label>
                      <div className="relative">
                        <input
                          id="main-media-upload"
                          type="file"
                          className="sr-only"
                          accept={
                            templateSchema.media_field.type === 'image'
                              ? 'image/*'
                              : templateSchema.media_field.type === 'video'
                                ? 'video/*'
                                : '*/*'
                          }
                          disabled={isCompressing}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) {
                              setMediaFile(null);
                              return;
                            }

                            if (
                              templateSchema.media_field.type === 'image' ||
                              file.type?.startsWith('image/')
                            ) {
                              if (file.size > MAX_IMAGE_SIZE_BYTES) {
                                toast.error(
                                  'Image size must be less than 8MB.',
                                );
                                e.target.value = '';
                                return;
                              }

                              try {
                                setIsCompressing(true);
                                const formData = new FormData();
                                formData.append('image', file);

                                const response = await axios.post(
                                  'https://compress.flexioninfotech.site/api/compress',
                                  formData,
                                  {
                                    headers: {
                                      'Content-Type': 'multipart/form-data',
                                    },
                                    responseType: 'blob',
                                  },
                                );

                                const blob = response.data;
                                const compressedFile = new File(
                                  [blob],
                                  file.name,
                                  {
                                    type: blob.type || 'image/jpeg',
                                  },
                                );
                                setMediaFile(compressedFile);
                                toast.success('Image compressed and selected.');
                              } catch (error) {
                                console.error(
                                  'Image compression failed, bypassing:',
                                  error,
                                );
                                // Bypass compressor and use original image if it fails
                                setMediaFile(file);
                              } finally {
                                setIsCompressing(false);
                              }
                            } else {
                              setMediaFile(file);
                            }
                          }}
                        />
                        <label
                          htmlFor="main-media-upload"
                          className={`inline-flex items-center px-4 py-2 rounded-lg border border-border-default text-sm font-semibold transition-all ${
                            isCompressing
                              ? 'bg-form-background-light text-text-muted cursor-not-allowed opacity-50'
                              : 'bg-white text-main-black hover:bg-surface-mint cursor-pointer'
                          }`}
                        >
                          <span className="truncate max-w-[200px]">
                            {mediaFile ? mediaFile.name : 'Choose file'}
                          </span>
                        </label>
                      </div>
                      {!mediaFile && (
                        <p className="text-xs text-red-500 font-medium">
                          {templateSchema.media_field.type === 'image'
                            ? 'Please upload the required image.'
                            : `Please upload the required ${templateSchema.media_field.label.toLowerCase()}.`}
                        </p>
                      )}
                      {isCompressing && (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary border-t-transparent"></div>
                          <span className="text-xs text-text-secondary font-bold">
                            Compressing image...
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {templateSchema.carousel_media?.length > 0 &&
                    templateSchema.carousel_media.map((mediaField) => (
                      <div
                        className="flex flex-col gap-2"
                        key={mediaField.cardIndex}
                      >
                        <label className="text-xs font-semibold text-text-secondary">
                          {mediaField.label}{' '}
                          <span className="text-primary">*</span>
                        </label>
                        <div className="relative">
                          <input
                            id={`carousel-media-upload-${mediaField.cardIndex}`}
                            type="file"
                            className="sr-only"
                            accept={
                              mediaField.type === 'image'
                                ? 'image/*'
                                : mediaField.type === 'video'
                                  ? 'video/*'
                                  : '*/*'
                            }
                            disabled={compressingCards[mediaField.cardIndex]}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) {
                                setCarouselMediaFiles((prev) => {
                                  const newFiles = { ...prev };
                                  delete newFiles[mediaField.cardIndex];
                                  return newFiles;
                                });
                                return;
                              }

                              if (
                                mediaField.type === 'image' ||
                                file.type?.startsWith('image/')
                              ) {
                                if (file.size > MAX_IMAGE_SIZE_BYTES) {
                                  toast.error(
                                    `Card ${mediaField.cardIndex + 1} image size must be less than 8MB.`,
                                  );
                                  e.target.value = '';
                                  return;
                                }

                                try {
                                  setCompressingCards((prev) => ({
                                    ...prev,
                                    [mediaField.cardIndex]: true,
                                  }));
                                  const formData = new FormData();
                                  formData.append('image', file);
                                  const response = await axios.post(
                                    'https://compress.flexioninfotech.site/api/compress',
                                    formData,
                                    {
                                      headers: {
                                        'Content-Type': 'multipart/form-data',
                                      },
                                      responseType: 'blob',
                                    },
                                  );
                                  const blob = response.data;
                                  const compressedFile = new File(
                                    [blob],
                                    file.name,
                                    { type: blob.type || 'image/jpeg' },
                                  );
                                  setCarouselMediaFiles((prev) => ({
                                    ...prev,
                                    [mediaField.cardIndex]: compressedFile,
                                  }));
                                  toast.success(
                                    `Image compressed for Card ${mediaField.cardIndex + 1}.`,
                                  );
                                } catch (error) {
                                  console.error(
                                    'Image compression failed, bypassing:',
                                    error,
                                  );
                                  setCarouselMediaFiles((prev) => ({
                                    ...prev,
                                    [mediaField.cardIndex]: file,
                                  }));
                                } finally {
                                  setCompressingCards((prev) => ({
                                    ...prev,
                                    [mediaField.cardIndex]: false,
                                  }));
                                }
                              } else {
                                setCarouselMediaFiles((prev) => ({
                                  ...prev,
                                  [mediaField.cardIndex]: file,
                                }));
                              }
                            }}
                          />
                          <label
                            htmlFor={`carousel-media-upload-${mediaField.cardIndex}`}
                            className={`inline-flex items-center px-4 py-2 rounded-lg border border-border-default text-sm font-semibold transition-all ${
                              compressingCards[mediaField.cardIndex]
                                ? 'bg-form-background-light text-text-muted cursor-not-allowed opacity-50'
                                : 'bg-white text-main-black hover:bg-surface-mint cursor-pointer'
                            }`}
                          >
                            <span className="truncate max-w-[200px]">
                              {carouselMediaFiles[mediaField.cardIndex]
                                ? carouselMediaFiles[mediaField.cardIndex].name
                                : 'Choose file'}
                            </span>
                          </label>
                        </div>
                        {!carouselMediaFiles[mediaField.cardIndex] && (
                          <p className="text-xs text-red-500 font-medium">
                            Please upload an image for Card{' '}
                            {mediaField.cardIndex + 1}.
                          </p>
                        )}
                        {compressingCards[mediaField.cardIndex] && (
                          <div className="flex items-center gap-2 mt-1">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary border-t-transparent"></div>
                            <span className="text-xs text-text-secondary font-bold">
                              Compressing image...
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                {templateSchema?.form_fields?.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 laptop:gap-2.5">
                    {templateSchema.form_fields
                      .filter((field, index) =>
                        template?.category === 'AUTHENTICATION'
                          ? index === 0
                          : true,
                      )
                      .map((field) => (
                        <div
                          key={field.id}
                          className="flex flex-col gap-1.5 laptop:gap-1"
                        >
                          <label
                            htmlFor={`template-var-${field.id}`}
                            className="text-xs laptop:text-[13px] font-semibold text-text-secondary"
                          >
                            {template?.category === 'AUTHENTICATION'
                              ? 'OTP Code'
                              : field.label}{' '}
                            <span className="text-primary">*</span>
                          </label>
                          <VariableValueInput
                            id={`template-var-${field.id}`}
                            value={templateValues[field.id] || ''}
                            placeholder={
                              template?.category === 'AUTHENTICATION'
                                ? 'Enter OTP code'
                                : 'Custom text'
                            }
                            options={
                              template?.category === 'AUTHENTICATION' ||
                              (recipientSummary.savedCount === 0 &&
                                recipientSummary.directCount > 0)
                                ? []
                                : DYNAMIC_CONTACT_FIELDS.filter(
                                    (contactField) =>
                                      !contactField.windex || windexConnected,
                                  ).map((contactField) => ({
                                    value: contactField.key,
                                    label: contactField.label,
                                  }))
                            }
                            onChange={(val) => {
                              if (template?.category === 'AUTHENTICATION') {
                                setTemplateValues((prev) => {
                                  const newVals = { ...prev };
                                  templateSchema.form_fields.forEach((f) => {
                                    newVals[f.id] = val;
                                  });
                                  return newVals;
                                });
                              } else {
                                setTemplateValues((prev) => ({
                                  ...prev,
                                  [field.id]: val,
                                }));
                              }
                            }}
                            onPickField={(key) =>
                              setTemplateValues((prev) => ({
                                ...prev,
                                [field.id]: contactFieldToken(key),
                              }))
                            }
                            inputClassName="laptop:!h-9"
                          />
                          {!templateValues[field.id]?.trim() && (
                            <p className="text-xs text-red-500 font-medium">
                              {template?.category === 'AUTHENTICATION'
                                ? 'OTP code is required.'
                                : `${field.label} is required.`}
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {!isSegmentAudience && (
            <div className="flex flex-col gap-3 laptop:gap-2 flex-1 min-h-0">
              {/* Recipient Source Tabs */}
              <div className="bg-white rounded-[12px] p-1.5 sm:p-2 laptop:p-1 flex flex-wrap items-center justify-between gap-2 laptop:gap-1.5 border border-border-default/60 shrink-0">
                <div className="flex items-center gap-1 sm:gap-1.5 p-1 bg-[#F5F5F5] rounded-lg">
                  <button
                    type="button"
                    onClick={() => setRecipientTab('contacts')}
                    className={`px-3 py-1.5 laptop:py-1 rounded-md text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 ${
                      recipientTab === 'contacts'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-text-secondary hover:text-main-black'
                    }`}
                  >
                    <img
                      src={
                        recipientTab === 'contacts'
                          ? employeesActiveIcon
                          : employeesIcon
                      }
                      alt=""
                      className="w-4 h-4 object-contain shrink-0"
                    />
                    <span>From Saved Contacts</span>
                    {savedSelectionCount > 0 && (
                      <span className="bg-primary/10 text-primary text-[11px] px-1.5 py-0.2 rounded-full font-bold">
                        {savedSelectionCount} Selected
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setRecipientTab('direct')}
                    className={`px-3 py-1.5 laptop:py-1 rounded-md text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 ${
                      recipientTab === 'direct'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-text-secondary hover:text-main-black'
                    }`}
                  >
                    <svg
                      className="w-4 h-4 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <span>Direct Numbers</span>
                    {directNumbers.length > 0 && (
                      <span className="bg-primary/10 text-primary text-[11px] px-1.5 py-0.2 rounded-full font-bold">
                        {directNumbers.length} Selected (direct)
                      </span>
                    )}
                  </button>
                </div>

                <div className="flex items-center gap-3 text-xs text-text-secondary px-2">
                  <span>
                    {recipientTab === 'contacts'
                      ? 'Selecting from your saved contact list'
                      : 'Sending to unsaved custom numbers'}
                  </span>
                  {matchGroups.map((group) => (
                    <span
                      key={group.key}
                      className="font-semibold text-text-primary"
                    >
                      {group.label} · Select All
                    </span>
                  ))}
                  {pickedIds.size > 0 && (
                    <span className="font-semibold text-text-primary">
                      +{pickedIds.size} manual
                    </span>
                  )}
                  {(matchGroups.length > 0 || pickedIds.size > 0) && (
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="font-semibold text-[#50AB3D] hover:text-primary-hover"
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
              </div>

              {/* Direct Numbers Card (shown if tab is 'direct') */}
              {recipientTab === 'direct' && (
                <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3 w-full border border-border-default/60 shrink-0 animate-fadeIn">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-main-black font-sf-pro-display">
                        Direct Contact Numbers (Unsaved)
                      </span>
                      {/* <span className="text-[11px] text-text-secondary font-normal">
                        (Not saved to contacts list)
                      </span> */}
                    </div>
                    {directNumbers.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearDirectNumbers}
                        className="text-xs font-semibold text-[#50AB3D] hover:text-primary-hover transition-colors"
                      >
                        Clear all ({directNumbers.length})
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      type="text"
                      value={directPhoneInput}
                      onChange={(e) => setDirectPhoneInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          handleAddDirectNumbers();
                        }
                      }}
                      placeholder="Enter mobile number with country code (e.g. 919876543210) & press Enter or comma"
                      className="flex-1 h-10 px-3 text-sm rounded-lg border border-border-default bg-[#F9FAFB] text-main-black placeholder:text-text-muted focus:outline-none focus:border-primary focus:bg-white transition-all"
                    />
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleAddDirectNumbers}
                      disabled={!directPhoneInput.trim()}
                      className="h-10 px-4 text-xs sm:text-sm font-semibold justify-center shrink-0"
                    >
                      + Add Number
                    </Button>
                  </div>

                  {directNumbers.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 max-h-32 overflow-y-auto pt-1">
                      {directNumbers.map((phone) => (
                        <span
                          key={phone}
                          className="inline-flex items-center gap-1.5 bg-[#EBF4EA] text-[#1B5E20] text-xs font-medium px-2.5 py-1 rounded-full border border-[#C8E6C9]"
                        >
                          <span>+{phone}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDirectNumber(phone)}
                            className="hover:text-rose-600 transition-colors p-0.5 rounded-full"
                            aria-label={`Remove ${phone}`}
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <line
                                x1="18"
                                y1="6"
                                x2="6"
                                y2="18"
                                strokeWidth="2.5"
                              />
                              <line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                                strokeWidth="2.5"
                              />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Saved Contacts Section (shown if tab is 'contacts') */}
              {recipientTab === 'contacts' && (
                <>
                  <div className="send-contacts-bar flex flex-wrap items-center gap-3 laptop:gap-2 shrink-0">
                    <SearchInput
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by Name or Phone"
                      className="w-full sm:w-auto sm:flex-1 sm:min-w-[220px]"
                      inputClassName="!h-11 laptop:!h-9 !bg-white"
                    />
                    {filterMessage ? (
                      <p className="w-full text-xs text-rose-600">
                        {filterMessage}
                      </p>
                    ) : null}
                    <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto shrink-0">
                      <div className="relative shrink-0" ref={filterMenuRef}>
                        <button
                          type="button"
                          onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                          aria-expanded={isFilterMenuOpen}
                          aria-haspopup="menu"
                          className={`relative h-11 laptop:h-9 px-4 rounded-[12px] text-sm font-semibold flex items-center gap-2 transition-colors shrink-0 focus:outline-none ${
                            isFilterMenuOpen || activeFilterFields.length > 0
                              ? 'bg-primary text-white hover:bg-primary-hover'
                              : 'bg-[#F5F5F5] text-[#1F2241] hover:brightness-95'
                          }`}
                        >
                          <FilterIcon className="w-4 h-4" />
                          Filter
                        </button>
                        {isFilterMenuOpen && (
                          <div
                            role="menu"
                            data-overlay-container="true"
                            style={{ overscrollBehavior: 'contain' }}
                            className="absolute left-0 top-[52px] z-40 w-56 bg-white border border-[#EBF4EA] rounded-xl shadow-xl py-2"
                          >
                            {windexConnected &&
                            WINDEX_FILTER_FIELDS.some(
                              (f) => !activeFilterFields.includes(f.key),
                            ) ? (
                              <div className="mx-2 mb-2 rounded-lg border border-[#C8E6C9] bg-[#F3FAF3] py-1">
                                <p className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#1B5E20]">
                                  <img
                                    src={windexIcon}
                                    alt=""
                                    className="h-3.5 w-3.5 rounded-sm object-contain"
                                  />
                                  Windex
                                </p>
                                {WINDEX_FILTER_FIELDS.filter(
                                  (f) => !activeFilterFields.includes(f.key),
                                ).map((f) => {
                                  const blocked = windexFilterBlocked(
                                    f.key,
                                    activeFilterFields,
                                  );
                                  return (
                                    <button
                                      key={f.key}
                                      type="button"
                                      role="menuitem"
                                      disabled={!!blocked}
                                      title={blocked || undefined}
                                      onClick={() =>
                                        handleAddFilterField(f.key)
                                      }
                                      className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors ${
                                        blocked
                                          ? 'cursor-not-allowed text-[#868686]'
                                          : 'text-[#1B5E20] hover:bg-[#E8F5E9]'
                                      }`}
                                    >
                                      {f.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                            <p className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide">
                              Filters
                            </p>
                            {FILTER_FIELDS.filter(
                              (f) => !activeFilterFields.includes(f.key),
                            ).map((f) => (
                              <button
                                key={f.key}
                                type="button"
                                role="menuitem"
                                onClick={() => handleAddFilterField(f.key)}
                                className="w-full text-left px-4 py-2 text-sm font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                              >
                                {f.label}
                              </button>
                            ))}
                            {activeFilterFields.length ===
                              FILTER_FIELDS.length +
                                (windexConnected
                                  ? WINDEX_FILTER_FIELDS.length
                                  : 0) && (
                              <p className="px-4 py-2 text-sm text-text-muted">
                                All filters added
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => setIsCreateContactOpen(true)}
                        className="h-11 laptop:h-9 px-3 sm:px-4 flex-1 sm:flex-initial sm:w-auto shrink-0 justify-center text-xs sm:text-sm"
                        icon={<PlusIcon />}
                      >
                        <span className="truncate">Create Contact</span>
                      </Button>
                    </div>
                  </div>

                  {activeFilterFields.length > 0 && (
                    <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                      {activeFilterFields.map((key, index) => {
                        const field =
                          FILTER_FIELDS.find((f) => f.key === key) ||
                          (windexConnected
                            ? WINDEX_FILTER_FIELDS.find((f) => f.key === key)
                            : null);
                        if (!field) return null;
                        const yearLocked =
                          key === 'class' && !windexFilters.year;
                        const yearRow = syncedYears.find(
                          (row) => row.year === windexFilters.year,
                        );
                        const classOptions = (yearRow?.classes || [])
                          .slice()
                          .sort(naturalSortAscending)
                          .map((name) => ({ value: name, label: name }));
                        return (
                          <Fragment key={key}>
                            {index > 0 && (
                              <span
                                className="flex items-center justify-center w-5 h-5 rounded-full bg-[#F5F5F5] text-[#868686] shrink-0"
                                aria-hidden="true"
                              >
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 4.5v15m7.5-7.5h-15"
                                  />
                                </svg>
                              </span>
                            )}
                            <FilterCard
                              key={key}
                              fieldKey={key}
                              label={field.label}
                              isWindex={WINDEX_FILTER_KEYS.has(key)}
                              hideValue={key === 'pendingFees'}
                              onRemove={() => handleRemoveFilterField(key)}
                            >
                              {key === 'customerType' && (
                                <CustomSelect
                                  value={typeFilter}
                                  onChange={(val) => {
                                    setTypeFilter(val);
                                    setPage(1);
                                  }}
                                  options={typeFilterOptions}
                                  searchable
                                  searchPlaceholder="Search type..."
                                  dropdownWidth={220}
                                  className="min-w-0 flex-1 sm:flex-initial"
                                  buttonClassName="h-8 px-2.5 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center justify-between gap-1 cursor-pointer focus:outline-none w-full sm:w-auto"
                                  textSizeClass="text-sm"
                                />
                              )}
                              {key === 'groups' && (
                                <CustomSelect
                                  value={segmentFilter}
                                  onChange={(val) => {
                                    setSegmentFilter(val);
                                    setPage(1);
                                  }}
                                  options={segmentFilterOptions}
                                  isLoading={isSegmentsLoading}
                                  searchable
                                  searchPlaceholder="Search groups..."
                                  dropdownWidth={220}
                                  className="min-w-0 flex-1 sm:flex-initial"
                                  buttonClassName="h-8 px-2.5 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center justify-between gap-1 cursor-pointer focus:outline-none w-full sm:w-auto"
                                  textSizeClass="text-sm"
                                />
                              )}
                              {key === 'importDate' && (
                                <div className="flex-1 min-w-0 flex justify-center">
                                  <CustomDateRangePicker
                                    startDate={dateStart}
                                    endDate={dateEnd}
                                    onApply={(start, end) => {
                                      setDateStart(start);
                                      setDateEnd(end);
                                      setPage(1);
                                    }}
                                    onClear={() => {
                                      setDateStart('');
                                      setDateEnd('');
                                      setPage(1);
                                    }}
                                    className="max-w-full min-w-0"
                                    triggerClassName="h-8 w-full sm:w-auto max-w-full min-w-0 px-2 sm:px-2.5 rounded-lg border-0 bg-[#F5F5F5] text-xs sm:text-sm font-medium justify-center gap-1.5 [&>span]:truncate [&>span]:min-w-0"
                                    iconSrc={datePickerCalendarIcon}
                                  />
                                </div>
                              )}
                              {key === 'year' && (
                                <CustomSelect
                                  value={windexFilters.year || ''}
                                  onChange={(val) => {
                                    setWindexFilters((prev) => ({
                                      ...prev,
                                      year: val || '',
                                      class: '',
                                    }));
                                    setPage(1);
                                  }}
                                  options={[
                                    { value: '', label: 'All years' },
                                    ...syncedYears.map((row) => ({
                                      value: row.year,
                                      label: row.year,
                                    })),
                                  ]}
                                  dropdownWidth={160}
                                  className="min-w-0 flex-1 sm:flex-initial"
                                  buttonClassName="h-8 px-2.5 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center justify-between gap-1 cursor-pointer focus:outline-none w-full sm:w-auto"
                                  textSizeClass="text-sm"
                                />
                              )}
                              {key === 'class' && (
                                <CustomSelect
                                  value={
                                    yearLocked ? '' : windexFilters.class || ''
                                  }
                                  disabled={
                                    yearLocked || syncedYears.length === 0
                                  }
                                  onChange={(val) => {
                                    setWindexFilters((prev) => ({
                                      ...prev,
                                      class: val || '',
                                    }));
                                    setPage(1);
                                  }}
                                  options={
                                    yearLocked
                                      ? []
                                      : [
                                          { value: '', label: 'All classes' },
                                          ...classOptions,
                                        ]
                                  }
                                  placeholder={
                                    yearLocked
                                      ? 'Select an academic year first'
                                      : undefined
                                  }
                                  dropdownWidth={180}
                                  className="min-w-0 flex-1 sm:flex-initial"
                                  buttonClassName="h-8 px-2.5 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center justify-between gap-1 cursor-pointer focus:outline-none w-full sm:w-auto"
                                  textSizeClass="text-sm"
                                />
                              )}
                              {key === 'srNo' && (
                                <input
                                  type="text"
                                  value={srNoFilter}
                                  onChange={(e) => {
                                    setSrNoFilter(e.target.value);
                                  }}
                                  placeholder="e.g. 5-12 or 5"
                                  className="h-8 min-w-0 flex-1 rounded-lg bg-[#F5F5F5] px-2.5 text-sm font-medium text-[#1F2241] focus:outline-none focus:ring-1 focus:ring-primary/40 sm:w-32 sm:flex-none"
                                />
                              )}
                            </FilterCard>
                          </Fragment>
                        );
                      })}

                      <button
                        type="button"
                        onClick={handleClearAllFilters}
                        className="text-sm font-semibold text-[#50AB3D] hover:text-primary-hover shrink-0 self-start sm:self-auto py-1"
                      >
                        Clear all
                      </button>
                    </div>
                  )}

                  <div className="send-contacts w-full min-w-0 flex flex-col flex-1 min-h-[420px] gap-2">
                    {parsedSrNo ? (
                      <span className="text-xs text-text-muted shrink-0">
                        Filtered Sr No.{' '}
                        <strong className="text-text-primary">
                          {parsedSrNo.start === parsedSrNo.end
                            ? parsedSrNo.start
                            : `${parsedSrNo.start} - ${parsedSrNo.end}`}
                        </strong>{' '}
                        ({count} contact{count !== 1 ? 's' : ''})
                      </span>
                    ) : null}
                    <V2Table
                      tight
                      columns={sendContactColumns}
                      data={
                        feesNeedYear || feesNeedClass ? [] : visibleContacts
                      }
                      rowKey="id"
                      storageKey="send_template_contacts_column_widths"
                      loading={
                        !feesNeedYear && !feesNeedClass && isContactsLoading
                      }
                      notice={
                        feesNeedYear || feesNeedClass
                          ? 'Select an Academic Year and Class to view pending fees.'
                          : null
                      }
                      empty={
                        windexFilters.year || windexFilters.class || feesOn
                          ? 'No contacts for this filter'
                          : 'No contacts found'
                      }
                      onRowClick={(contact) => toggleSelection(contact.id)}
                      isRowSelected={(contact) => isRowSelected(contact)}
                      pagination={{
                        currentPage: page,
                        totalCount: count,
                        itemsPerPage: 10,
                        onChange: setPage,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Phone Preview & Actions */}
        <div
          className={`w-full shrink-0 self-start flex flex-col gap-4 ${className.includes('groups-send-wa') ? 'lg:w-[388px]' : 'lg:w-[330px] xl:w-[350px] 2xl:w-[365px] lg:sticky lg:top-0'}`}
        >
          <div
            className={`w-full flex flex-col items-center justify-center rounded-2xl border border-surface-mint bg-form-background-light p-3 sm:p-4 min-[1366px]:p-4 laptop:p-2 ${className.includes('groups-send-wa') ? '' : 'overflow-hidden'}`}
          >
            {className.includes('groups-send-wa') ? (
              <TemplatePhonePreview
                template={template}
                templateValues={templateValues}
                message=""
                mediaFile={mediaFile}
                carouselMediaFiles={carouselMediaFiles}
              />
            ) : (
              <>
                <style>{`
              @media (min-width: 1024px) and (max-width: 1440px) and (max-height: 899px) {
                .send-template-phone-scale {
                  position: absolute;
                  left: 50%;
                  top: 0;
                  width: 340px;
                  margin-left: -170px;
                  transform: scale(0.75);
                  transform-origin: top center;
                }
              }
            `}</style>
                <div className="relative mx-auto w-full max-w-[340px] laptop:h-[495px] laptop:w-[255px] laptop:max-w-[255px] laptop:overflow-hidden">
                  <div className="send-template-phone-scale">
                    <TemplatePhonePreview
                      template={template}
                      templateValues={templateValues}
                      message=""
                      mediaFile={mediaFile}
                      carouselMediaFiles={carouselMediaFiles}
                      className="my-auto w-full max-w-[340px] !h-[580px] sm:!h-[620px] xl:!h-[660px]"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Bottom Action */}
          <div className="flex flex-col gap-2 w-full min-w-0 shrink-0">
            <Button
              variant="secondary"
              onClick={onCancel}
              className="h-11 px-4 font-semibold w-full justify-center"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSend}
              disabled={
                isSending ||
                isCompressing ||
                !isTemplateReady ||
                (isSegmentAudience
                  ? (Number(segmentAudience?.contactCount) || 0) <= 0
                  : totalRecipients === 0)
              }
              className="h-11 px-4 font-semibold w-full min-w-0 justify-center whitespace-normal text-center disabled:!opacity-40 disabled:!cursor-not-allowed disabled:!pointer-events-none"
            >
              {isSending ? (
                isScheduleMode ? (
                  'Scheduling...'
                ) : (
                  'Sending...'
                )
              ) : (
                <>
                  <span>{getSendButtonLabel()}</span>
                  <span>→</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <SideDrawer
        isOpen={isCreateContactOpen}
        onClose={() => {
          if (!createContactMutation.isPending) setIsCreateContactOpen(false);
        }}
        width="lg"
        showClose={false}
        bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
        headerClassName="!px-6 !py-5"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <h2 className="text-[20px] leading-[28px] font-semibold text-main-black font-sf-pro-display">
              Create Contact
            </h2>
            <button
              type="button"
              onClick={() => {
                if (!createContactMutation.isPending)
                  setIsCreateContactOpen(false);
              }}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
      >
        <ContactForm
          customerTypes={customerTypes}
          isPending={createContactMutation.isPending}
          onSubmit={(payload) => createContactMutation.mutate(payload)}
        />
      </SideDrawer>
    </div>
  );
}
